import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Settings as SettingsIcon, Cog, Lock, Banknote, Save, Trash2, AlertTriangle, RefreshCw, Eye, MessageCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import WaStatusPill from "../../components/WaStatusPill";
import { invalidatePlatformWa } from "../../lib/usePlatformWa";

const FIELDS = [
  { key: "account_holder", label: "Account holder name" },
  { key: "bank_name", label: "Bank name" },
  { key: "account_number", label: "Account number" },
  { key: "iban", label: "IBAN" },
  { key: "branch", label: "Branch (optional)" },
  { key: "jazzcash_number", label: "JazzCash number (optional)" },
  { key: "easypaisa_number", label: "Easypaisa number (optional)" },
];

export default function AdminSettings() {
  const [bank, setBank] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/platform/bank").then((r) => setBank(r.data || {}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/bank", bank);
      setBank(data);
      toast.success("Bank details saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">System Settings</h1>
        <p className="text-slate-400 mt-1">Global configuration. More controls coming soon.</p>
      </div>

      <form onSubmit={save} className="bg-[#151A23] border border-slate-800 rounded-md p-6 space-y-4">
        <div className="flex items-center gap-2 text-slate-200">
          <Banknote className="w-5 h-5 text-emerald-400" />
          <div className="font-display font-semibold text-lg">Our Bank Details</div>
        </div>
        <p className="text-xs text-slate-500">
          These details are shown to shop owners on their <b>Subscription / Pay Now</b> screen so they can transfer subscription payments.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input
                value={bank[f.key] || ""}
                onChange={(e) => setBank({ ...bank, [f.key]: e.target.value })}
                className="bg-slate-900 border-slate-700 text-slate-200"
                data-testid={`platform-bank-${f.key}`}
              />
            </div>
          ))}
        </div>
        <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="platform-bank-save">
          <Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save bank details"}
        </Button>
      </form>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-[#151A23] border border-slate-800 rounded-md p-6">
          <div className="flex items-center gap-2 text-slate-200">
            <Cog className="w-5 h-5 text-amber-400" />
            <div className="font-display font-semibold text-lg">Defaults</div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-400">
            <li>· Default late fee: <span className="text-slate-200 tabular">PKR 200/day</span></li>
            <li>· Reminder timing: <span className="text-slate-200">3 days before due, on due, +7 overdue</span></li>
            <li>· Currency: <span className="text-slate-200">PKR</span></li>
            <li>· Trial period on plan upgrade approval: <span className="text-slate-200">+7 days</span></li>
          </ul>
        </div>

        <div className="bg-[#151A23] border border-slate-800 rounded-md p-6">
          <div className="flex items-center gap-2 text-slate-200">
            <Lock className="w-5 h-5 text-red-400" />
            <div className="font-display font-semibold text-lg">Security</div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate-400">
            <li>· JWT expiry: <span className="text-slate-200">7 days</span></li>
            <li>· Impersonation token expiry: <span className="text-slate-200">1 hour</span></li>
            <li>· Bcrypt rounds: <span className="text-slate-200">default (12)</span></li>
            <li>· Upload limit: <span className="text-slate-200">10 MB</span></li>
          </ul>
        </div>

        <div className="bg-[#151A23] border border-slate-800 rounded-md p-6 lg:col-span-2">
          <div className="flex items-center gap-2 text-slate-200">
            <SettingsIcon className="w-5 h-5 text-sky-400" />
            <div className="font-display font-semibold text-lg">Referral Partner Program</div>
          </div>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-400">
            <li>· Joining fee: <span className="text-slate-200 tabular">PKR 0 (Free)</span></li>
            <li>· Referral reward: <span className="text-slate-200 tabular">PKR 500 fixed</span></li>
            <li>· Subscription price: <span className="text-slate-200 tabular">PKR 1,500 flat</span></li>
            <li>· Minimum withdrawal: <span className="text-slate-200 tabular">PKR 500</span></li>
            <li>· Activation: <span className="text-slate-200 tabular">Instant</span></li>
            <li>· Referral code format: <span className="text-slate-200 tabular">FIRSTNAME-NNNN</span></li>
          </ul>
        </div>
      </div>

      <PlatformWaCard />

      <DataCleanupCard />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Platform WhatsApp Business Settings
// ────────────────────────────────────────────────────────────────────────
function PlatformWaCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/platform/whatsapp").then((r) => setData(r.data || {}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        support_number: data.support_number,
        support_display: data.support_display,
        support_message: data.support_message,
        welcome_template: data.welcome_template,
      };
      const r = await api.put("/admin/settings/whatsapp", payload);
      toast.success("Platform WhatsApp settings saved");
      // Invalidate cache so all surfaces (float, banners, landing footer) pick
      // up the new values on next render / navigation.
      invalidatePlatformWa();
      // re-fetch to reflect normalized digits + connected flag
      const fresh = await api.get("/platform/whatsapp");
      setData(fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const num = (data.support_number || "").replace(/\D/g, "");
  const connected = num.length >= 10 && num.length <= 15;
  const openWa = () => window.open(`https://wa.me/${num}`, "_blank");
  const sendTest = () => {
    const msg = data.support_message || "Test message from HisaabPro admin";
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <form
      onSubmit={save}
      className="bg-[#151A23] border border-emerald-500/20 ring-1 ring-emerald-500/10 rounded-md p-6 space-y-4"
      data-testid="admin-platform-wa-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-slate-200">
          <MessageCircle className="w-5 h-5 text-emerald-400" />
          <div>
            <div className="font-display font-semibold text-lg">Platform WhatsApp Business Settings</div>
            <p className="text-xs text-slate-500">
              Official HisaabPro WhatsApp account jo har shop, distributor aur landing page par dikhta hai (deep-link only — no API).
            </p>
          </div>
        </div>
        <WaStatusPill connected={connected} testIdPrefix="platform-wa" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-slate-300">Official WhatsApp Business Number (E.164 digits, no +)</Label>
          <Input
            value={data.support_number || ""}
            onChange={(e) => setData({ ...data, support_number: e.target.value })}
            placeholder="923718067478"
            inputMode="numeric"
            className="bg-slate-900 border-slate-700 text-slate-200"
            data-testid="admin-wa-number-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Display label (shown in UI)</Label>
          <Input
            value={data.support_display || ""}
            onChange={(e) => setData({ ...data, support_display: e.target.value })}
            placeholder="+92 371 8067478"
            className="bg-slate-900 border-slate-700 text-slate-200"
            data-testid="admin-wa-display-input"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-300">Default Support Message (prefilled when user opens chat)</Label>
        <Input
          value={data.support_message || ""}
          onChange={(e) => setData({ ...data, support_message: e.target.value })}
          className="bg-slate-900 border-slate-700 text-slate-200"
          data-testid="admin-wa-support-msg"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-slate-300">Welcome Message Template (sent after verification approval)</Label>
        <Textarea
          rows={4}
          value={data.welcome_template || ""}
          onChange={(e) => setData({ ...data, welcome_template: e.target.value })}
          className="bg-slate-900 border-slate-700 text-slate-200 font-mono text-sm"
          data-testid="admin-wa-welcome-tmpl"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="admin-wa-save">
          <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving..." : "Save Number"}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!connected}
          onClick={openWa}
          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
          data-testid="admin-wa-open"
        >
          <ExternalLink className="w-4 h-4 mr-1.5" /> Open WhatsApp
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={!connected}
          onClick={sendTest}
          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
          data-testid="admin-wa-test"
        >
          <MessageCircle className="w-4 h-4 mr-1.5" /> Send Test Message
        </Button>
      </div>
    </form>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Data Cleanup — admin-only "Clean Test Data" with preview + confirm
// ────────────────────────────────────────────────────────────────────────
function DataCleanupCard() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/admin/cleanup/preview");
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup/execute", { confirm: "DELETE_TEST_DATA" });
      setLastResult(data);
      toast.success(`Cleanup complete · ${data.deleted.shops} shops, ${data.deleted.distributors} distributors`);
      setConfirmOpen(false);
      setConfirmText("");
      // Re-load preview (should now show 0)
      await loadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const total = preview?.counts
    ? Object.values(preview.counts).reduce((a, b) => a + (b || 0), 0)
    : 0;
  const hasNothing = preview && total === 0;

  return (
    <div
      className="bg-[#151A23] border border-red-900/50 rounded-md p-6 ring-1 ring-red-900/30"
      data-testid="data-cleanup-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-200">
          <Trash2 className="w-5 h-5 text-red-400" />
          <div className="font-display font-semibold text-lg">Data Cleanup</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold bg-red-500/10 text-red-300 border border-red-500/30 px-2 py-0.5 rounded">
          danger zone
        </span>
      </div>
      <p className="text-sm text-slate-400 mt-2">
        Bulk-remove test/demo shops, distributors, and all their cascading data
        (customers, loans, payments, logs, commissions). Platform admin, plans,
        settings, and real (non-test) accounts are <b className="text-slate-200">never touched</b>.
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-400">
        <Rule label="Test email domains" value="@example.com, @test.com, …" />
        <Rule label="Name pattern" value="contains 'test' as a token (e.g., TEST_Foo)" />
        <Rule label="Explicit flag" value="is_test == true" />
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          variant="outline"
          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
          data-testid="cleanup-preview-btn"
        >
          {loadingPreview ? (
            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1" />
          )}
          {preview ? "Refresh preview" : "Preview test data"}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={!preview || hasNothing}
          className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
          data-testid="cleanup-execute-btn"
        >
          <Trash2 className="w-4 h-4 mr-1" /> Clean Test Data
        </Button>
      </div>

      {preview && (
        <div className="mt-5 space-y-3" data-testid="cleanup-preview-panel">
          {hasNothing ? (
            <div className="text-sm text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 rounded p-3">
              ✓ Nothing to clean — system is already free of test data.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Object.entries(preview.counts).map(([k, v]) => (
                  <div
                    key={k}
                    data-testid={`cleanup-count-${k}`}
                    className={`rounded border px-3 py-2 ${
                      v > 0
                        ? "bg-red-500/5 border-red-900/40 text-slate-200"
                        : "bg-slate-900 border-slate-800 text-slate-500"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{k.replace(/_/g, " ")}</div>
                    <div className="font-display tabular text-lg font-bold">{v}</div>
                  </div>
                ))}
              </div>

              {preview.test_shops_sample.length > 0 && (
                <details className="bg-slate-900 border border-slate-800 rounded p-3 text-sm" open={false} data-testid="cleanup-shops-details">
                  <summary className="cursor-pointer text-slate-300 font-semibold">
                    Test shops to be removed ({preview.counts.shops})
                  </summary>
                  <div className="mt-2 max-h-60 overflow-auto text-xs">
                    {preview.test_shops_sample.map((s) => (
                      <div key={s.id} className="flex items-center justify-between gap-2 border-b border-slate-800 py-1.5">
                        <span className="text-slate-200 font-medium truncate">{s.name}</span>
                        <span className="text-slate-500 truncate">{s.owner_email}</span>
                        <span className="text-slate-600 text-[10px] whitespace-nowrap">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {preview.test_distributors_sample.length > 0 && (
                <details className="bg-slate-900 border border-slate-800 rounded p-3 text-sm" open={false} data-testid="cleanup-distributors-details">
                  <summary className="cursor-pointer text-slate-300 font-semibold">
                    Test distributors to be removed ({preview.counts.distributors})
                  </summary>
                  <div className="mt-2 max-h-60 overflow-auto text-xs">
                    {preview.test_distributors_sample.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-2 border-b border-slate-800 py-1.5">
                        <span className="text-slate-200 font-medium truncate">{d.name}</span>
                        <span className="text-slate-500 truncate">{d.email}</span>
                        <span className="text-slate-600 text-[10px] whitespace-nowrap">{d.reason}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}

      {lastResult && (
        <div
          className="mt-4 bg-emerald-500/5 border border-emerald-500/30 rounded p-3 text-sm text-emerald-300"
          data-testid="cleanup-result"
        >
          ✓ Last cleanup · {lastResult.deleted.shops} shops · {lastResult.deleted.distributors} distributors ·{" "}
          {lastResult.deleted.users} users · {lastResult.deleted.customers} customers ·{" "}
          {lastResult.deleted.loans} loans · {lastResult.deleted.payments} payments removed.{" "}
          <span className="text-emerald-500 font-mono text-xs">backup id: {lastResult.cleanup_id?.slice(0, 8)}…</span>
        </div>
      )}

      {/* Confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={(v) => { setConfirmOpen(v); if (!v) setConfirmText(""); }}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <AlertDialogTitle className="text-center">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              <span className="block">
                This action <b>cannot be undone</b>. {preview?.counts.shops || 0} test shops,{" "}
                {preview?.counts.distributors || 0} test distributors and all their cascading data
                ({(preview?.counts.customers || 0) + (preview?.counts.loans || 0) + (preview?.counts.payments || 0) + (preview?.counts.wa_logs || 0)} rows)
                will be permanently deleted.
              </span>
              <span className="block mt-3 text-xs text-slate-500">
                A backup of deleted shop/distributor metadata is written to <code>cleanup_backups</code> for audit.
              </span>
              <span className="block mt-3 text-left">
                Type <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-700 font-bold">DELETE_TEST_DATA</code> to confirm:
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE_TEST_DATA"
            className="font-mono"
            data-testid="cleanup-confirm-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runCleanup}
              disabled={confirmText !== "DELETE_TEST_DATA" || cleaning}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cleanup-confirm-execute"
            >
              {cleaning ? "Cleaning…" : "Yes, delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Rule({ label, value }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="font-mono text-slate-300 truncate">{value}</div>
    </div>
  );
}
